import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'

import { cn } from '@/lib/utils'

type PagePaneLayoutMode = 'scroll' | 'fill'

type PagePaneLayoutContextValue = {
  mode: PagePaneLayoutMode
  setFillLayout: (enabled: boolean) => void
}

const PagePaneLayoutContext = createContext<PagePaneLayoutContextValue | null>(null)

export function PagePaneLayoutProvider({ children }: { children: ReactNode }) {
  const [fillRequestCount, setFillRequestCount] = useState(0)
  const mode: PagePaneLayoutMode = fillRequestCount > 0 ? 'fill' : 'scroll'

  const value = useMemo(
    () => ({
      mode,
      setFillLayout: (enabled: boolean) => {
        setFillRequestCount((count) => (enabled ? count + 1 : Math.max(0, count - 1)))
      },
    }),
    [mode],
  )

  return <PagePaneLayoutContext.Provider value={value}>{children}</PagePaneLayoutContext.Provider>
}

/** When enabled, the page pane is a fixed-height flex column; only inner regions may scroll. */
export function usePagePaneFillLayout(enabled: boolean) {
  const ctx = useContext(PagePaneLayoutContext)
  useLayoutEffect(() => {
    if (!ctx || !enabled) return
    ctx.setFillLayout(true)
    return () => ctx.setFillLayout(false)
  }, [enabled, ctx])
}

export function usePagePaneLayoutMode(): PagePaneLayoutMode {
  return useContext(PagePaneLayoutContext)?.mode ?? 'scroll'
}

export function PagePaneScrollRegion({
  scrollRef,
  children,
  className,
}: {
  scrollRef: RefObject<HTMLDivElement | null>
  children: ReactNode
  className?: string
}) {
  const mode = usePagePaneLayoutMode()

  useLayoutEffect(() => {
    if (mode === 'fill') {
      scrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [mode, scrollRef])

  return (
    <div
      ref={scrollRef}
      data-page-pane-scroll=""
      className={cn(
        'flex-1 min-h-0 @container',
        mode === 'fill'
          ? 'flex flex-col overflow-hidden'
          : cn(
              'overflow-y-auto overscroll-y-contain touch-pan-y custom-scrollbar',
              className,
            ),
      )}
    >
      {mode === 'fill' ? (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">{children}</div>
      ) : (
        children
      )}
    </div>
  )
}
